import Vue from 'vue';
import THREEService from 'services/three-js-service';
import ComponentDatabase from 'services/component-database';
import Component from 'modules/component';
import l10n from 'services/l10n-service';

const THREE = require('THREE');

function Product(type, json) {
  if (json) {
    this.type = json.type;
  } else {
    this.type = type;
  }

  this._modelRoot = new THREE.Group();

  this._manifest = Product.Manifests[this.type] || {};
  this._availableComponentTypes = Object.keys(this._manifest)
    .reduce((result, componentType) => {
      result[componentType] = true;
      return result;
    }, {});

  if (json) {
    this._componentData = this._createComponentData(json.data, this._manifest);
  } else {
    this._componentData = this._randomComponentData(this._manifest);
  }
  this._components = this._createComponents(this._componentData);

  this._components.root.getModel().then((model) => {
    this._modelRoot.add(model);
  });

  this._dimensions = {
    width: 0,
    height: 0,
    depth: 0,
    seatDepth: 0,
    seatHeight: 0
  };
}

Product.Type = {
  Table: 0,
  Chair: 1
};

Product.Manifests = {
  get [Product.Type.Table]() {
    return {
      table_top: {
        min: 1,
        max: 1
      },
      chair_leg: {
        requiredComponent: 'table_top',
        min: 4,
        max: 4
      },
      center_chair_leg: {
        requiredComponent: 'table_top',
        min: 0,
        max: 1
      }
    };
  },
  get [Product.Type.Chair]() {
    return {
      seat: {
        min: 1,
        max: 1
      },
      chair_leg: {
        requiredComponent: 'seat',
        min: 4,
        max: 4
      },
      center_chair_leg: {
        requiredComponent: 'seat',
        min: 0,
        max: 1
      },
      back_rest: {
        requiredComponent: 'back_support',
        min: 0,
        max: 1
      },
      back_support: {
        requiredComponent: 'seat',
        min: 0,
        max: 1
      },
      arm_rest: {
        requiredComponent: 'seat',
        min: 0,
        max: 2
      }
    };
  }
};

Product.Warnings = {
  remove_back_support: 'remove_back_support_warning',
  use_combination_chair_leg: 'use_combination_chair_leg_warning',
  use_combination_seat: 'use_combination_seat_warning'
};

Product.createFromJson = function createFromJson(json) {
  return new Product(json.type, json);
};

Product.prototype = {
  get manifest() {
    return this._manifest;
  },
  get components() {
    return this._components;
  },
  get model() {
    return this._modelRoot;
  },
  get dimensions() {
    return this._dimensions;
  },
  get availableComponentTypes() {
    return this._availableComponentTypes;
  }
};

Product.prototype._getRandomComponentIdOfType =
  function _getRandomComponentIdOfType(type, isOptional) {
    const componentsDataOfType = ComponentDatabase.getComponentsByType(type);
    const componentsDataCount = componentsDataOfType.length;
    let index;

    if (isOptional) {
      index = Math.round(Math.random() * componentsDataCount);
    } else {
      index = Math.round(Math.random() * (componentsDataCount - 1));
    }

    if (index < componentsDataCount) {
      return componentsDataOfType[index].id;
    }
    return null;
  };

// temp function
Product.prototype._getRandomVariationValues =
  function _getRandomVariationValues(component) {
    if (!component.variations) {
      return {};
    }

    const variationValues = {};

    component.variations.forEach((variation) => {
      const option = variation.options[
        Math.round(Math.random() * 10000) % variation.options.length];
      variationValues[variation.id] = option.value;
    });

    return variationValues;
  };

Product.prototype._randomComponentData =
  function _randomComponentData(manifest) {
    const hasBackRest = Math.random() > 0.5;
    const useCenterChairLeg = Math.random() > 0.5;
    const componentData = {};

    // Generate a random product
    Object.keys(manifest).forEach((componentType) => {
      const componentManifest = manifest[componentType];
      let requiredCount = 0;

      if (componentType === 'back_support' || componentType === 'back_rest') {
        requiredCount = hasBackRest ? 1 : 0;
      } else if (componentType === 'chair_leg') {
        requiredCount = useCenterChairLeg ? 0 : 4;
      } else if (componentType === 'center_chair_leg') {
        requiredCount = useCenterChairLeg ? 1 : 0;
      } else {
        requiredCount = componentManifest.min +
          (Math.round(Math.random() * 10000) %
          (componentManifest.max - componentManifest.min + 1));
      }

      const components = componentData[componentType] = [];
      for (let i = 0; i < requiredCount; i += 1) {
        const componentId = this._getRandomComponentIdOfType(componentType);
        const component = ComponentDatabase.getComponent(componentId);

        component.variationValues = this._getRandomVariationValues(component);
        components.push(component);
      }
    });

    return componentData;
  };

Product.prototype._createComponentData = function _createComponentData(data,
manifest) {
  const componentData = {};

  Object.keys(manifest).forEach((componentType) => {
    if (data[componentType]) {
      componentData[componentType] = data[componentType].map((componentObj) => {
        const component = ComponentDatabase.getComponent(componentObj.id);
        component.variationValues =
          Object.assign({}, componentObj.variationValues);
        return component;
      });
    } else {
      // Assign an empty array for vuejs to watch.
      componentData[componentType] = [];
    }
  });

  return componentData;
};

Product.prototype.toJson = function toJson() {
  const json = {
    type: this.type
  };
  const data = {};

  Object.keys(this._componentData).forEach((componentType) => {
    const components = this._components[componentType];
    data[componentType] = components.map((component) => {
      // get the current variation values
      const variationValues = {};
      Object.keys(component.variations).forEach((variationName) => {
        variationValues[variationName] =
          component.getVariation(variationName);
      });

      return {
        id: component.id,
        variationValues
      };
    });
  });

  json.data = data;
  return json;
};

Product.prototype._connectComponentsOfType = function _connectComponentsOfType(
  rootComponent, components, componentType) {
  const junctions = rootComponent.getJunctionsOfType(componentType, true);
  const componentsOfType = components[componentType];

  return Promise.all(componentsOfType.map((component, index) => {
    const junction = junctions && junctions[index];
    if (junction) {
      return junction.connect(component);
    } else {
      // remove the component if it is not connected.
      return this.removeComponent(componentType, index);
    }
  }));
};

Product.prototype._createComponents =
  function _createComponents(componentData) {
    const components = {};
    const connectPromises = [];
    let rootComponent = null;
    let connectFunc;

    // Create component objects
    Object.keys(componentData).forEach((componentType) => {
      components[componentType] = componentData[componentType].map((datum) => {
        const component = Component.createFromJson(datum);
        const variationValues = datum.variationValues;

        Object.keys(variationValues).forEach((variationName) => {
          component.setVariation(variationName,
            variationValues[variationName]);
        });

        return component;
      });
    });

    switch (this.type) {
      case Product.Type.Chair:
        rootComponent = components.seat[0];
        connectFunc = this._connectComponentsOfType.bind(this, rootComponent,
          components);

        connectPromises.push(connectFunc('chair_leg'));
        connectPromises.push(connectFunc('center_chair_leg'));
        connectPromises.push(connectFunc('arm_rest'));

        // back_rest depends on back support
        connectPromises.push(connectFunc('back_support').then(() => {
          return connectFunc('back_rest');
        }));
        break;
      case Product.Type.Table:
        rootComponent = components.table_top[0];
        connectFunc = this._connectComponentsOfType.bind(this, rootComponent,
          components);

        connectPromises.push(connectFunc('chair_leg'));
        connectPromises.push(connectFunc('center_chair_leg'));
        break;
      default:
        console.warn('Invalid product type');
        return null;
    }

    Promise.all(connectPromises).then(() => {
      this._updateDimension();
      this._updateAvailableComponentTypes();
    });

    components.root = rootComponent;
    return components;
  };

Product.prototype._updateDimension = function _updateDimension() {
  let productBundingBox;

  this._components.root.getModel().then((model) => {
    productBundingBox = new THREE.Box3().setFromObject(model);
    this._dimensions = {
      width: Math.round(productBundingBox.max.x - productBundingBox.min.x),
      // XXX: The height of the current models of chair legs is less than the
      // real one by 20mm. Add it back here.
      height:
        Math.round(productBundingBox.max.y - productBundingBox.min.y) + 20,
      depth: Math.round(productBundingBox.max.z - productBundingBox.min.z),
      seatDepth: 0,
      seatHeight: 0
    };
  }).then(() => {
    const nullPromise = Promise.resolve(null);
    const promises = [nullPromise, nullPromise, nullPromise];

    // get seat
    if (this._components.seat && this._components.seat[0]) {
      promises[0] = this._components.seat[0].getModel();
    }

    // get back rest
    if (this._components.back_rest && this._components.back_rest[0]) {
      promises[1] = this._components.back_rest[0].getModel();
    }

    // get leg
    let chairLeg = this._components.center_chair_leg &&
      this._components.center_chair_leg[0];
    if (chairLeg == null && this._components.chair_leg &&
      this._components.chair_leg.length) {
      chairLeg = this._components.chair_leg.find((leg) => {
        return leg != null;
      });
    }
    if (chairLeg != null) {
      promises[2] = chairLeg.getModel();
    }

    return Promise.all(promises);
  }).then((models) => {
    const seatModel = models[0];
    const backRestModel = models[1];
    const chairLegModel = models[2];

    let seatBox;
    let backRestBox;
    let chairLegBox;

    if (seatModel) {
      seatBox = THREEService.getBoundingBoxOfModelSelf(seatModel);
    }
    if (backRestModel) {
      backRestBox = THREEService.getBoundingBoxOfModelSelf(backRestModel);
    }
    if (chairLegModel) {
      chairLegBox = THREEService.getBoundingBoxOfModelSelf(chairLegModel);
    }

    if (seatModel && chairLegModel) {
      this._dimensions.seatDepth = Math.round(seatBox.getSize().z);
      // XXX: The height of the current models of chair legs is less than the
      // real one by 20mm. Add it back here.
      this._dimensions.seatHeight =
        Math.round(seatBox.union(chairLegBox).getSize().y) + 20;
    }

    if (backRestModel) {
      this._dimensions.seatDepth =
        Math.round(productBundingBox.max.z - backRestBox.max.z);
    } else if (seatModel) {
      this._dimensions.seatDepth = Math.round(seatBox.max.z - seatBox.min.z);
    }
  });
};

/**
 * Make sure that the component required by the one being added exists.
 *
 * @param {String} componentType
 * @param {String} componentId
 * @returns {Promise}
 */
Product.prototype._ensureComponentReplacement =
  function _ensureComponentReplacement(componentType) {
    switch (componentType) {
      case 'back_rest':
        if (!this.components.back_support || !this.components.back_support[0]) {
          // Add a random back support
          const supportId = this._getRandomComponentIdOfType('back_support');
          return this.replaceComponent('back_support', 0, supportId);
        }
        break;
      case 'center_chair_leg':
        // remove all chair legs if the user chooses center chair legs
        if (this.components.chair_leg && this.components.chair_leg.length) {
          return Promise.all(this.components.chair_leg.map((chairLeg, i) => {
            return this.removeComponent('chair_leg', i);
          }));
        }
        break;
      case 'chair_leg':
        // remove the center chair leg if the user chooses ordinary chair legs
        if (this.components.center_chair_leg &&
          this.components.center_chair_leg.length) {
          return this.removeComponent('center_chair_leg', 0);
        }
        break;
      default:
        break;
    }
    return Promise.resolve();
  };

Product.prototype._displayWarningMessage =
  function _displayWarningMessage(componentType, componentId, confirm) {
    if (!confirm) {
      return Promise.resolve();
    }

    let confirmMsg = '';

    // remove a component
    // request confirmation first
    if (componentId == null) {
      switch (componentType) {
        case 'back_support':
          if (this._components.back_rest && this._components.back_rest[0]) {
            confirmMsg = l10n.get(Product.Warnings.remove_back_support);
          }
          break;
        default:
          break;
      }

      if (confirmMsg) {
        return confirm(confirmMsg).then((isOk) => {
          return isOk ? Promise.resolve() : Promise.reject('User cancel');
        });
      } else {
        return Promise.resolve();
      }
    }

    // replace a component
    // check if this is a combination component using component id
    const combinationComponentsByType =
      ComponentDatabase.getCombinationComponentsByType(componentType);
    if (!combinationComponentsByType ||
      !combinationComponentsByType.find((component) => {
        return component.id === componentId;
      })) {
      return Promise.resolve();
    }

    switch (componentType) {
      case 'seat':
        if (this._components.back_support &&
          this._components.back_support[0]) {
          confirmMsg = l10n.get(Product.Warnings.use_combination_seat);
        }
        break;
      case 'center_chair_leg':
        if (this._components.chair_leg &&
          this._components.chair_leg[0]) {
          confirmMsg =
            l10n.get(Product.Warnings.use_combination_chair_leg);
        }
        break;
      default:
        break;
    }

    if (confirmMsg) {
      return confirm(confirmMsg).then((isOk) => {
        return isOk ? Promise.resolve() : Promise.reject('User cancel');
      });
    } else {
      return Promise.resolve();
    }
  };

Product.prototype._updateAvailableComponentTypes =
  function _updateAvailableComponentTypes() {
    if (this.type === Product.Type.Chair) {
      this._availableComponentTypes.back_support =
      this._availableComponentTypes.back_rest =
      !!this._components.root.getJunctionsOfType('back_support').length;
    }
  };

Product.prototype.replaceComponent =
  function replaceComponent(componentType, index, componentId, confirm) {
    // index is for specifying one of the components of the same type.
    console.log(`${componentType} ${index} ${componentId}`);

    if (this.components[componentType][index] &&
        this.components[componentType][index].id === componentId) {
      return Promise.resolve();
    }

    if (componentId == null) {
      return this.removeComponent(componentType, index, confirm);
    }

    let originalComponent;
    let newComponent;
    let targetJunction;

    return this._displayWarningMessage(componentType, componentId, confirm)
    .then(() => {
      const newComponentData = ComponentDatabase.getComponent(componentId);
      newComponent = Component.createFromJson(newComponentData);

      // Set the data as soon as possible to reflect the new state on UI.
      // replace the component data
      Vue.set(this._componentData[componentType], index, newComponentData);
      Vue.set(this._components[componentType], index, newComponent);

      // Delay the subsequent operations so the UI update won't be blocked.
      return new Promise((resolve) => {
        setTimeout(resolve);
      }).then(() => {
        return this._ensureComponentReplacement(componentType);
      });
    }).then(() => {
      if (newComponent.type === this._components.root.type) {
        originalComponent = this._components.root;
      } else {
        // Find the junction that the component to be replaced is currently
        // connected to.
        const junctions =
          this._components.root.getJunctionsOfType(newComponent.type, true);
        targetJunction = junctions[index];

        if (targetJunction) {
          originalComponent = targetJunction.childComponent;
        } else {
          return Promise.reject('No matched junction');
        }
      }

      return Component.moveChildComponents(originalComponent, newComponent);
    }).then((droppedComponents) => {
      droppedComponents = droppedComponents || [];
      // clean up the dropped components during the movement.
      return Promise.all(droppedComponents.map((droppedComponent) => {
        const droppedType = droppedComponent.component.type;
        const droppedIndex = droppedComponent.index;
        return this.removeComponent(droppedType, droppedIndex);
      }));
    })
    .then(() => {
      if (targetJunction) {
        return targetJunction.connect(newComponent);
      } else {
        return Promise.all([
          newComponent.getModel(),
          this._components.root.getModel()
        ]).then((models) => {
          const newModel = models[0];
          const originalModel = models[1];

          this._modelRoot.add(newModel);
          this._modelRoot.remove(originalModel);
          this._components.root = newComponent;
        });
      }
    })
    .catch((error) => {
      console.warn(error);
    })
    .then(() => {
      this._updateDimension();
      this._updateAvailableComponentTypes();
    });
  };

Product.prototype.removeComponent =
  function removeComponent(componentType, index, confirm) {
    return this._displayWarningMessage(componentType, null, confirm)
    .then(() => {
      // remove the component data
      Vue.set(this._componentData[componentType], index, null);
      Vue.set(this._components[componentType], index, null);

      // Delay the subsequent operations so the UI update won't be blocked.
      return new Promise((resolve) => {
        setTimeout(resolve);
      });
    })
    .then(() => {
      // remove related component first.
      switch (componentType) {
        case 'back_support':
          return this.removeComponent('back_rest', 0);
        default:
          return Promise.resolve();
      }
    })
    .then(() => {
      const junctions =
        this._components.root.getJunctionsOfType(componentType, true);
      const targetJunction = junctions[index];

      if (targetJunction) {
        return targetJunction.disconnect();
      } else {
        return Promise.resolve();
      }
    })
    .then(() => {
      this._updateDimension();
      this._updateAvailableComponentTypes();
    })
    .catch((error) => {
      console.warn(error);
    });
  };

export default Product;



// WEBPACK FOOTER //
// ./src/modules/product.js